import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { some } from "@antv/util";

// ── Overlays ──────────────────────────────────────────────────────────────────

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  padding: theme.spacing(4),
}));

const NoRowsOverlay = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      gap: 1,
      color: "#94a3b8",
    }}
  >
    <PersonOffOutlinedIcon sx={{ fontSize: "48px" }} />
    <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
      No Data Found
    </Typography>
  </Box>
);

const CustomLoadingOverlay = () => (
  <StyledGridOverlay>
    <CircularProgress />
  </StyledGridOverlay>
);

// ── Status chip config ────────────────────────────────────────────────────────

const STATUS_CHIP_STYLES = {
  Active: { bg: "#E8F5E9", color: "#4CAF50" },
  Qualified: { bg: "#E8F5E9", color: "#4CAF50" },
  Closed: { bg: "#feeade", color: "#FF7924" },
  Cancelled: { bg: "#FFEBEE", color: "#C62828" },
  Inactive: { bg: "#ececec", color: "#616161" },
  Draft: { bg: "#fef3e7", color: "#F57F17" },
  "On Hold": { bg: "#fcf4e1", color: "#F7A800" },
  Full: { bg: "#fafde9", color: "#8D9E44" },
  Applied: { bg: "#ecf7fe", color: "#2196F3" },
  "Employer Applied": { bg: "#edf7ff", color: "#1565C0" },
  Shortlisted: { bg: "#E8F5E9", color: "#2E7D32" },
  "Tech Evaluation": { bg: "#fef3e7", color: "#FB8C00" },
  "Manager Evaluation": { bg: "#E0F7FA", color: "#006064" },
  "HR Round": { bg: "#E0F7FA", color: "#006064" },
  "Offer Released": { bg: "#E8F5E9", color: "#2E7D32" },
  Withdrawn: { bg: "#FFEBEE", color: "#C62828" },
  Rejected: { bg: "#f8e5e5", color: "#D32F2F" },
  Verified: { bg: "#E8F5E9", color: "#2E7D32" },
  Unverified: { bg: "#FFEBEE", color: "#C62828" },
  Paused: { bg: "#fff3e0", color: "#F57C00" },
};

const StatusChip = ({ label }) => {
  if (!label)
    return (
      <Typography
        variant="body2"
        sx={{ color: "#000", padding: "10px 24px", fontSize: "14px" }}
      >
        NA
      </Typography>
    );
  const style = STATUS_CHIP_STYLES[label] || {
    bg: "#F5F5F5",
    color: "#424242",
  };

  let icon = null;

  if (label === "Verified") {
    icon = <img src="/assets/verified.svg" alt="" />;
  } else if (label === "Unverified") {
    icon = <img src="/assets/unverified.svg" alt="" />;
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 2,
        py: 0.6,
        gap: "4px",
        borderRadius: "20px",
        background: style.bg,
        color: style.color,
        fontSize: "13px",
        fontWeight: 400,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </Box>
  );
};

const JOB_TYPE_MAP = {
  H: "Hire",
  C: "Contract",
  C2H: "Contract to Hire",
  F: "Full-Time",
};
const mapJobType = (val) => JOB_TYPE_MAP[val] ?? val ?? "NA";

const verifiedColors = { 1: "success", 0: "error" };

const cell = (sx = {}) => ({
  sx: { color: "#000", fontSize: "14px", fontWeight: 400, ...sx },
});

const safeVal = (value) =>
  value === null || value === undefined || value === "" ? "NA" : value;

const COLUMN_SETS = {
  Shortlisted: [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      filterable: true,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {safeVal(p.row.id)}
        </Typography>
      ),
    },
    {
      field: "fullname",
      headerName: "Full Name",
      width: 200,
      filterable: false,
      renderCell: (p) => (
        <Typography
          variant="body2"
          title={p.row.fullname}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "#000",
          }}
        >
          {safeVal(p.row.fullname)}
        </Typography>
      ),
    },
    {
      field: "entered_by",
      headerName: "Created By",
      width: 160,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {safeVal(p.row.entered_by)}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Date Created",
      width: 160,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {p.row.created_at
            ? new Date(p.row.created_at).toLocaleDateString("en-US")
            : "NA"}
        </Typography>
      ),
    },
    {
      field: "current_location",
      headerName: "Location",
      width: 160,
      filterable: false,
      renderCell: (p) => (
        <Typography
          variant="body2"
          title={p.row.current_location}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "#000",
          }}
        >
          {safeVal(p.row.current_location)}
        </Typography>
      ),
    },
    {
      field: "date_created",
      headerName: "Shortlisted On",
      width: 160,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {p.row.date_created
            ? new Date(p.row.date_created).toLocaleDateString("en-US")
            : "NA"}
        </Typography>
      ),
    },
    {
      field: "isVerified",
      headerName: "Verification Status",
      width: 180,
      filterable: false,
      renderCell: (p) => (
        <Box>
          <StatusChip label={p.row.isVerified ? "Verified" : "Unverified"} />
        </Box>
      ),
    },
    {
      field: "last_active",
      headerName: "Last Active On",
      width: 150,
      filterable: false,
      renderCell: (p) => (
        <Typography
          variant="body2"
          title={p.row.last_active}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "#000",
          }}
        >
          {safeVal(p.row.last_active)}
        </Typography>
      ),
    },
  ],

  Applicants: [
    {
      field: "id",
      headerName: "ID",
      width: 110,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {safeVal(p.row.id)}
        </Typography>
      ),
    },
    {
      field: "fullname",
      headerName: "Full Name",
      width: 180,
      filterable: false,
      renderCell: (p) => (
        <Typography
          variant="body2"
          title={p.row.fullname}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "#000",
          }}
        >
          {safeVal(p.row.fullname)}
        </Typography>
      ),
    },
    {
      field: "entered_by",
      headerName: "Created By",
      width: 130,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {safeVal(p.row.entered_by)}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Date Created",
      width: 150,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {p.row.created_at
            ? new Date(p.row.created_at).toLocaleDateString("en-US")
            : "NA"}
        </Typography>
      ),
    },
    {
      field: "current_location",
      headerName: "Location",
      width: 130,
      filterable: false,
      renderCell: (p) => (
        <Typography
          variant="body2"
          title={p.row.current_location}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "#000",
          }}
        >
          {safeVal(p.row.current_location)}
        </Typography>
      ),
    },
    {
      field: "date_modified",
      headerName: "Applied On",
      width: 150,
      filterable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {p.row.date_modified
            ? new Date(p.row.date_modified).toLocaleDateString("en-US")
            : "NA"}
        </Typography>
      ),
    },
    {
      field: "source",
      headerName: "Source",
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Typography variant="body2" {...cell()}>
          {safeVal(p.row.source)}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Box>
          <StatusChip label={p.row.status} />
        </Box>
      ),
    },
    {
      field: "isVerified",
      headerName: "Verification Status",
      width: 150,
      filterable: false,
      renderCell: (p) => (
        <StatusChip label={p.row.isVerified ? "Verified" : "Unverified"} />
      ),
    },
    {
      field: "notes",
      headerName: "Last Activity",
      width: 225,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Typography
          variant="body2"
          title={p.row.notes}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "#000",
          }}
        >
          {safeVal(p.row.notes)}
        </Typography>
      ),
    },
  ],
};

const getRouteColumns = (pathname, name) => {
  if (name && COLUMN_SETS[name]) return COLUMN_SETS[name];

  if (pathname === "/Find-talents")
    return [
      { field: "id", headerName: "ID", width: 100 },
      { field: "name", headerName: "Full Name", width: 200 },
      { field: "entered_by", headerName: "Created By", width: 200 },
      { field: "created_at", headerName: "Date Created", width: 200 },
      { field: "current_location", headerName: "Location", width: 200 },
      {
        field: "experience",
        headerName: "Experience",
        width: 200,
        renderCell: (params) => {
          const year = params.row.year;
          const month = params.row.month;
          let exp = "";
          if (year) exp += `${year} year${year > 1 ? "s" : ""} `;
          if (month) exp += `${month} month${month > 1 ? "s" : ""}`;
          return exp || "NA";
        },
      },
      { field: "current_pay", headerName: "Current Salary", width: 250 },
      { field: "date_created", headerName: "Last Active On", width: 250 },
    ];

  if (pathname === "/Company")
    return [
      { field: "id", headerName: "ID", width: 150, sortable: true },
      { field: "name", headerName: "Company Name", width: 200, sortable: true },
      {
        field: "branch_count",
        headerName: "Branch Count",
        width: 200,
        sortable: true,
      },
      { field: "city", headerName: "City", width: 150, sortable: true },
      { field: "state", headerName: "State", width: 150, sortable: true },
      { field: "phone1", headerName: "Mobile", width: 200, sortable: true },
      {
        field: "date_created",
        headerName: "Created On",
        width: 200,
        sortable: true,
      },
    ];

  if (pathname === "/jobOrders")
    return [
      { field: "id", headerName: "ID", width: 150, sortable: true },
      {
        field: "title",
        headerName: "Job Title",
        width: 200,
        sortable: true,
        renderCell: (p) => (
          <Tooltip title={p.row.title || "NA"} arrow>
            <Typography
              variant="body2"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                color: "#000",
                fontSize: "14px",
                width: "100%",
              }}
            >
              {p.row.title || "NA"}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "type",
        headerName: "Job Type",
        width: 200,
        renderCell: (p) => (
          <Typography variant="body2" {...cell()}>
            {mapJobType(p.row.type)}
          </Typography>
        ),
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (p) => (
          <Box>
            <StatusChip label={p.row.status} />
          </Box>
        ),
        sortable: true,
      },
      {
        field: "date_created",
        headerName: "Created On",
        width: 200,
        sortable: true,
      },
      {
        field: "count",
        headerName: "Applications",
        width: 200,
        sortable: true,
      },
      {
        field: "open_count",
        headerName: "Open Applications",
        width: 200,
        sortable: true,
      },
      {
        field: "status_rejected_count",
        headerName: "Rejected Applications",
        width: 200,
        sortable: true,
      },
    ];

  if (pathname === "/Manage-Candidates")
    return [
      { field: "id", filterable: true, headerName: "ID", width: 150 },
      {
        field: "fullname",
        filterable: true,
        headerName: "Full Name",
        width: 200,
      },
      {
        field: "entered_by",
        filterable: true,
        headerName: "Created By",
        width: 200,
      },
      {
        field: "date_created",
        filterable: true,
        headerName: "Date Created",
        width: 200,
      },
      {
        field: "current_pay",
        filterable: true,
        headerName: "Salary",
        width: 250,
      },
      {
        field: "current_location",
        filterable: true,
        headerName: "Location",
        width: 200,
      },
      {
        field: "Experience",
        filterable: true,
        headerName: "Experience",
        width: 200,
      },
    ];

  if (pathname === "/Users")
    return [
      { field: "id", headerName: "ID", width: 150 },
      {
        field: "full_name",
        headerName: "Full Name",
        width: 200,
        renderCell: (p) => {
          const fullName =
            `${p.row.first_name || ""} ${p.row.last_name || ""}`.trim();
          return (
            <Typography
              variant="body2"
              title={fullName}
              sx={{
                textTransform: "capitalize",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                color: "#000",
                fontSize: "14px",
              }}
            >
              {fullName || "NA"}
            </Typography>
          );
        },
      },
      { field: "role", headerName: "Role", width: 200 },
      {
        field: "status",
        headerName: "Status",
        width: 200,
        renderCell: (p) => (
          <Box>
            <StatusChip label={p.row.status} />
          </Box>
        ),
      },
      { field: "assigned_under", headerName: "Assigned Under", width: 250 },
      { field: "email", headerName: "Email", width: 200 },
      {
        field: "companies_associated",
        headerName: "Companies Associated",
        width: 200,
      },
      { field: "job_count", headerName: "Jobs Created By Users", width: 200 },
      {
        field: "candidate_count",
        headerName: "Candidates Created By Users",
        width: 200,
      },
    ];

  return [];
};

// ── Main Component ────────────────────────────────────────────────────────────
const DynamicDataGrid = ({
  rows = [],
  loading = false,
  showCheckbox = false,
  showSelectedCount = true,
  onRowSelection = () => {},
  onRowSelectionModelChange,
  rowSelectionModel,
  formatters = {},
  totalRows = 0,
  paginationModel = { page: 0, pageSize: 10 },
  onPaginationModelChange,
  setShowAllData,
  handleOpen,
  buttonName,
  hideFooter = false,
  name,
  columns: columnsProp,
  sortingMode,
  onSortModelChange,
}) => {
  const location = useLocation();
  const safePaginationModel = {
    page: paginationModel?.page ?? 0,
    pageSize: paginationModel?.pageSize ?? 10,
  };

  const getFormattedCellValue = (value, row, field) => {
    if (value === null || value === undefined || value === "") return "NA";
    const dateFields = [
      "created_at",
      "date_created",
      "date_modified",
      "Date Joined",
      "Last Active",
      "Last Profile Updated",
      "Date Posted",
      "Applied On",
      "Shortlisted On",
      "Last Active On",
      "Created On",
      "Last Job Applied",
      "timestamp",
      "date",
    ];
    if (
      dateFields.includes(field) &&
      typeof value === "string" &&
      !isNaN(Date.parse(value))
    ) {
      return dayjs(value).format("DD-MMM-YYYY");
    }
    return formatters[field] ? formatters[field](value, row) : value;
  };

  const columns = columnsProp?.length
    ? columnsProp
    : getRouteColumns(location.pathname, name);

  const updatedColumns = columns.map((col) => ({
    ...col,
    renderCell: col.renderCell
      ? col.renderCell
      : (params) => getFormattedCellValue(params.value, params.row, col.field),
  }));

  const isLogsPage = location.pathname === "/logs";

  const emitPaginationChange = (nextModel) => {
    if (!onPaginationModelChange) return;
    const nextPage = nextModel?.page ?? safePaginationModel.page;
    const nextPageSize = nextModel?.pageSize ?? safePaginationModel.pageSize;
    if (
      nextPage === safePaginationModel.page &&
      nextPageSize === safePaginationModel.pageSize
    ) {
      return;
    }
    onPaginationModelChange({ page: nextPage, pageSize: nextPageSize });
  };
  // starting 

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        
      }}
    >
      <DataGrid
        rows={rows}
        loading={loading}
        columns={updatedColumns}
        paginationModel={safePaginationModel}
        onPaginationModelChange={emitPaginationChange}
        pageSizeOptions={[10, 20, 50, 100]}
        checkboxSelection={showCheckbox}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        pagination
        paginationMode="server"
        rowCount={totalRows}
        hideFooter={hideFooter}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowClick={(params) => {
          if (!params.isSelected) onRowSelection(params.row);
        }}
        hideFooterSelectedRowCount={showSelectedCount}
        getRowHeight={isLogsPage ? () => "auto" : undefined}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 1 ? "row-alternate" : ""
        }
        sortingMode={sortingMode || "client"}
        onSortModelChange={onSortModelChange}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
          noRowsOverlay: NoRowsOverlay,
          pagination: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(0,0,0,0.6)", fontSize: "14px" }}
                >
                  Rows per page:
                </Typography>
                <select
                  value={safePaginationModel.pageSize}
                  onChange={(e) =>
                    emitPaginationChange({
                      ...safePaginationModel,
                      pageSize: Number(e.target.value),
                      page: 0,
                    })
                  }
                  style={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontSize: "14px",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </Box>
              <Pagination
                count={Math.ceil(totalRows / safePaginationModel.pageSize) || 1}
                page={safePaginationModel.page + 1}
                onChange={(e, page) =>
                  emitPaginationChange({
                    ...safePaginationModel,
                    page: page - 1,
                  })
                }
                shape="rounded"
                color="primary"
                siblingCount={1}
                boundaryCount={1}
                size="small"
              />
            </Box>
          ),
        }}
        sx={{
          border: "1px solid rgba(115, 119, 145, 0.2)",
          borderRadius: "15px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
            { display: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#D9EAFB",
            borderRadius: "15px 15px 0 0",
            color: "#151D48",
            borderBottom: "none",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            minHeight: "45px !important",
            maxHeight: "56px !important",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#D9EAFB",
            display: "flex",
            alignItems: "center",
            padding: "0 24px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "14px",
            whiteSpace: "normal",
            lineHeight: "1.2",
            textAlign: "left",
            width: "100%",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 400,
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "rgb(245, 245, 245)" },
          },
          "& .MuiDataGrid-row.row-alternate": {
            backgroundColor: "#FBFBFB",
            "&:hover": { backgroundColor: "rgb(245, 245, 245)" },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #F1F5F9",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "10px 24px",
            gap: "7.831px",
            color: "#333",
            fontSize: "14px",
            fontWeight: 400,
            outline: "none !important",
            ...(isLogsPage && { padding: "8px" }),
          },
          "& .MuiDataGrid-cellCheckbox": {
            padding: "0px !important",
            justifyContent: "center",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #E2E8F0",
            backgroundColor: "#fff",
            borderRadius: "0 0 15px 15px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 8px",
          },
          "& .MuiPaginationItem-root": { borderRadius: "4px" },
          "& .MuiDataGrid-main": { flex: 1, minHeight: 0, overflow: "hidden" },
          "& .MuiDataGrid-virtualScroller": {
            overflowY: "auto !important",
            flex: 1,
          },
        }}
      />
    </Box>
  );
};

export default DynamicDataGrid;
